import { effect } from '@preact/signals';
import {
  clearTimeout as clearWorkerTimeout,
  setTimeout as setWorkerTimeout,
} from 'worker-timers';
import {
  currentDuration,
  currentEpisode,
  currentSeek,
  isFixingAudio,
  playbackRate,
  state,
} from '../global-state/audio-signals';
import { getAdSkipsLeft, trackAdSkipUnlessRecent } from '../global-state/usage-data';
import { allowAdSkipOrPlayFailedToSkipAdSound } from '../utils/gate-ad-skip';
import { exposeOnWindow } from '../utils/helpers';
import { isIOSNativeApp } from '../utils/platform';
import VanillaAudioPlayer from '../utils/vanilla-audio-player';
import debugLog from './debug';

//
// Setup async loop to update currentSeek and skip ads
//

const log = (...args: any[]) => {
  debugLog({ logName: 'audio-state', logColor: 'darkblue' }, ...args);
};

// How deep into ad we can be before we no longer automatically skip it
export const nextAdThresholdSeconds = 2;

export function setupAudioPlayingLoop(
  player: {
    updateSeek: () => Promise<void>;
    playFailToSkipAdSound: () => void;
  },
  seekTo: (seconds: number, reason: string) => void
) {
  const lastSeekWhilePlaying: { seek: number; timestamps: number[] } = {
    seek: -1,
    timestamps: [],
  };
  exposeOnWindow({ laskSeek: lastSeekWhilePlaying });

  type PendingAdSkip = {
    ad: { startTime: number; endTime: number };
    whenMs: number;
  };

  let timeoutId: number;
  const scheduleAudioLoop = (timeoutMs: number, pendingAdSkip?: PendingAdSkip) => {
    if (timeoutId) clearWorkerTimeout(timeoutId);
    timeoutId = setWorkerTimeout(() => audioPlayingLoop(pendingAdSkip), timeoutMs);
  };

  effect(() => {
    // Start loop when audio starts playing
    // Handle "paused" in case we go from paused -> seeking -> paused
    if (state.value === 'playing' && !isFixingAudio.value) {
      // Don't track dependencies within loop
      scheduleAudioLoop(0);
    }
    // Reset timestamps whenever state changes
    lastSeekWhilePlaying.timestamps = [];
  });

  // Whether to try and skip ads at all or let native app do it
  const isAdSkippingEnabled = () =>
    !(
      // On iOS app 1.1.* and later the native app will skip all ads
      (isIOSNativeApp && window.iosAppVersion && window.iosAppVersion >= 1.1)
    );
  exposeOnWindow({ isAdSkippingEnabled });

  async function audioPlayingLoop(pendingAdSkip?: PendingAdSkip) {
    if (pendingAdSkip && isAdSkippingEnabled()) {
      const timeToNextAdMs = pendingAdSkip.whenMs - Date.now();
      log('Start of loop w/ pending ad', timeToNextAdMs);
      skipAd(pendingAdSkip.ad);
      return;
    }

    // Handle iOS app native ad-skipping edge case where, if the episode isn't downloaded and there's an ad at the end that iOS skips,
    // iOS will send a correct seek to client, but then if client requests a seek after that it'll be inaccurate.
    if (
      isIOSNativeApp &&
      state.value === 'paused' &&
      !isAdSkippingEnabled() &&
      currentSeek.value >= currentDuration.value - 1
    ) {
      log('Avoid requesting seek on iOS after end-of-episode ad-skip');
      return;
    }

    // Update seek
    await player.updateSeek();
    const seek = currentSeek.value;

    // Not playing: no need to continue to update seek or skip ads
    if (state.value !== 'playing') return;

    // Fixing audio: no need to loop
    if (isFixingAudio.value) return;

    if (lastSeekWhilePlaying.seek === seek) {
      // Duplicate seek: record more timestamps
      lastSeekWhilePlaying.timestamps.push(Date.now());
      const { timestamps } = lastSeekWhilePlaying;
      if (timestamps.length > 1) {
        const interval = timestamps[timestamps.length - 1] - timestamps[0];
        log('Duplicate seek while playing:', seek, interval);
        if (interval > 2000) {
          if (player instanceof VanillaAudioPlayer && !player.hasRecentlyFixedAudio) {
            player.fixAudio('playing', seek, 'seek not changing');
          }
        }
      }
    } else {
      // Update to new seek value
      lastSeekWhilePlaying.seek = seek;
      lastSeekWhilePlaying.timestamps = [Date.now()];
    }

    // Align render time w/ seek seconds so UI is as accurate as possible
    // With worker timeout, we add a tiny bit to ensure it happens after the second tick
    // and we divide by the playback rate
    const timeToNextSecondMs =
      (Math.round(1000 - ((seek * 1000) % 1000)) + 5) / playbackRate.value;

    // We're already nearly at the second mark. No need to check again real soon
    const timeToWaitUntilNextAudioLoop =
      timeToNextSecondMs < 50 ? timeToNextSecondMs + 1000 : timeToNextSecondMs;

    // Find future ad (or ad that started recently)
    const { ads } = currentEpisode.value || {};

    const nextAd =
      ads && ads.find(({ startTime }) => startTime >= seek - nextAdThresholdSeconds);
    if (nextAd && isAdSkippingEnabled()) {
      // If ad needs to be skipped within the next second or so, skip it!
      const timeToNextAdMs = Math.round(1000 * (nextAd.startTime - seek));
      const readyToSkipAd =
        timeToNextAdMs < Math.max(1000, timeToWaitUntilNextAudioLoop + 200);

      // Log if time to next ad is less than 10 seconds (for debugging ad skip failures)
      if (timeToNextAdMs < 10 * 1000) {
        log({
          toAd: timeToNextAdMs,
          ready: readyToSkipAd,
        });
      }

      if (readyToSkipAd) {
        // In the rare case that we've already passed the ad, skip it now
        if (timeToNextAdMs <= 0) return skipAd(nextAd);

        // We schedule audio loop with a queued ad instead of using setTimeout() b/c the
        // audio loop uses a worker timer which is more accurate
        scheduleAudioLoop(Math.max(0, timeToNextAdMs - 10), {
          ad: nextAd,
          whenMs: Date.now() + timeToNextAdMs,
        });
        return;
      }
    }

    // Keep looping
    scheduleAudioLoop(timeToWaitUntilNextAudioLoop);
  }

  async function skipAd({
    startTime,
    endTime,
  }: {
    startTime: number;
    endTime: number;
  }) {
    if (!isAdSkippingEnabled()) return;

    // If user has no ad skips left, play failed to skip ad sound and don't skip ad
    const adSkipCreditsLeft = getAdSkipsLeft().total;
    if (!allowAdSkipOrPlayFailedToSkipAdSound(player)) {
      // Always continue audio loop
      scheduleAudioLoop(1000);
      return;
    }

    // Seek (and continue audio loop as soon as state is set back to playing)
    log('Skipping ad', { adSkipCreditsLeft });
    seekTo(endTime, 'skip ad');

    // TODO -> have native app report back what the seek was at the time of skipping
    // this is the only reliable way to know if I'm actually scheduling for the right time

    // Track ad skip
    const episode = currentEpisode.peek();
    trackAdSkipUnlessRecent(episode!.id, startTime, endTime);
  }
}
